import { useState, useEffect, useMemo } from "react";
import {
  BodyComponent,
  TableComponent,
  trans,
  HeaderComponent,
  RefreshButton,
  CreateButton
} from "@comps/components";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { Commons, Const } from "../constants";
import { Button, Dropdown, Form, Input, message, Popconfirm, Tooltip } from 'antd';
import { PaginationComponent } from "@comps/components";
import { deleteDataReq, getAllDataReq, getReportsFromJobsReq, getReportSchedulesReq, getReportTypesAndTables } from "./requests";
import { useSelector } from "react-redux";
import CreateRawReport from "./components/CreateRawReport";
import UpdateRawReport from "./components/UpdateRawReport";
import CreateCustomReport from "./components/CreateCustomReport";
import {
  CalendarOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from "lodash";
import CopyReportToSaas from "./components/CopyReportToSaas";
import CreatePredefinedReport from "./components/CreatePredefinedReport";
import ScheduleReport from "./components/ScheduleReport";
import { checkPermissions } from "../../../wrappers/commonFunctions";
import UpdateReportName from "./components/UpdateReportName";
import ScheduledReportsManagement from "./components/ScheduledReportsManagement";
import { useWorkspacePageTabs } from "../../../containers/components/navigation/ModuleWorkspace";
import { getReportListPath, getReportScope, getReportTypeKeysForScope } from "../reportScopes";
import "./all-reports.css";

const langKey = Const["allReports"].lngKey;
const pageConfig = {
  headers: {
    // The toolbar switcher already names the view, and the breadcrumb keeps the
    // page identity, so the big "All Reports" heading is dropped.
    createEntity: "Report",
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const["allReports"].route
      }
    ]
  }
}

const categoryStyles = {
  employee: { key: "employee", label: "Employee", color: "#3B6B8F", soft: "#E0EAF1" },
  payroll: { key: "payroll", label: "Payroll", color: "#B5821A", soft: "#F6EAD0" },
  attendance: { key: "attendance", label: "Attendance", color: "#3F7D58", soft: "#E3EFE7" },
  neutral: { key: "neutral", label: "Other", color: "#A39B8D", soft: "#F3F0EB" },
};

const getReportCategory = (name = "") => {
  const normalizedName = name.toLowerCase();
  if (normalizedName.includes("employee")) return categoryStyles.employee;
  if (normalizedName.includes("payroll") || normalizedName.includes("salary") || normalizedName.includes("provident")) return categoryStyles.payroll;
  if (normalizedName.includes("attendance")) return categoryStyles.attendance;
  return categoryStyles.neutral;
};

const getReportTypeCount = (reportType, fallbackCounts) => {
  const explicitCount = reportType.reports_count ?? reportType.report_count ?? reportType.count;
  if (Number.isFinite(Number(explicitCount))) return Number(explicitCount);
  if (Array.isArray(reportType.reports)) return reportType.reports.length;
  return fallbackCounts[reportType.id] || 0;
};

const IndexAllReports = (props) => {
  const navigate = useNavigate()
  const location = useLocation();
  const reportScope = getReportScope(props.scope);
  const reportScopeKeys = useMemo(() => getReportTypeKeysForScope(props.scope), [props.scope]);
  const reportListPath = getReportListPath(props.scope);
  const pageHeaders = useMemo(() => {
    if (!reportScope) return pageConfig.headers;

    return {
      ...pageConfig.headers,
      title: reportScope.label,
      breadcrumb: [
        {
          name: reportScope.label,
          path: reportListPath,
        },
      ],
    };
  }, [reportListPath, reportScope]);
  const initialSelectedReport = location.state?.selectedReport;
  const authUser = useSelector((state) => state.auth.authUser);
  const [searchForm] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loader, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1)
  const [searchInput, setSearchInput] = useState(initialSelectedReport?.title || '');
  const [searchTerm, setSearchTerm] = useState(initialSelectedReport?.title || '');
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(initialSelectedReport?.id || null);
  const [reportTypes, setReportTypes] = useState([]);
  const [reportTypeSearch, setReportTypeSearch] = useState("");
  const [activeTab, setActiveTab] = useState("reports");
  const [scheduledReportsCount, setScheduledReportsCount] = useState(0);
  const [pagination, setPagination] = useState({
    "total": 0,
    "count": 0,
    "per_page": 0,
    "current_page": 0,
    "total_pages": 0,
    "links": {}
  });

  // ==================================
  //   Use States End
  // ==================================

  // ==================================
  //   Table Data End
  // ==================================
  const reportTypeFallbackCounts = useMemo(() => {
    return dataSource.reduce((counts, report) => {
      const typeId = report.report_type_id || report.report_type?.id;
      if (!typeId) return counts;
      return {
        ...counts,
        [typeId]: (counts[typeId] || 0) + 1,
      };
    }, {});
  }, [dataSource]);

  const selectedReportTypeMeta = useMemo(() => {
    return reportTypes.find((reportType) => reportType.id === selectedReportType);
  }, [reportTypes, selectedReportType]);

  const scopedReportTypes = useMemo(() => {
    if (!reportScopeKeys.length) return reportTypes;
    return reportTypes.filter((reportType) => reportScopeKeys.includes(reportType.key));
  }, [reportScopeKeys, reportTypes]);

  const filteredReportTypes = useMemo(() => {
    const search = reportTypeSearch.trim().toLowerCase();
    if (!search) return scopedReportTypes;
    return scopedReportTypes.filter((reportType) => reportType.name?.toLowerCase().includes(search));
  }, [reportTypeSearch, scopedReportTypes]);

  const reportTypeOptions = useMemo(() => {
    return [
      {
        id: null,
        name: reportScope?.typeLabel || "All Reports",
        count: pagination.total || 0,
        category: categoryStyles.neutral,
      },
      ...filteredReportTypes.map((reportType) => ({
        ...reportType,
        count: getReportTypeCount(reportType, reportTypeFallbackCounts),
        category: getReportCategory(reportType.name),
      })),
    ];
  }, [filteredReportTypes, pagination.total, reportScope?.typeLabel, reportTypeFallbackCounts]);

  const activeFilterLabel = selectedReportTypeMeta?.name || reportScope?.typeLabel || "All Reports";
  const activeFilterCount = selectedReportType
    ? getReportTypeCount(selectedReportTypeMeta || {}, reportTypeFallbackCounts)
    : pagination.total || 0;

  const columns = [

    {
      key: 'title',
      title: trans(`${langKey}.title`),
      render: (record) => {

        return (
          <Tooltip title="Generate Report">
            <button
              type="button"
              className="reports-all__name-button"
              onClick={ () => generateReport(record) }
            >
              <span className="reports-all__doc-tile">
                <FileTextOutlined />
              </span>
              <span className="reports-all__name-text">{ record.title }</span>
            </button>
          </Tooltip>
        )
      }
    },

    {
      key: 'type',
      title: trans(`${langKey}.report_type_name`),
      render: (record) => {
        const typeName = record.report_type?.name || record.report_type_name || "-";
        const category = getReportCategory(typeName);
        return (
          <span
            className={ `reports-all__type-badge reports-all__type-badge--${category.key}` }
            style={ { "--report-type-color": category.color, "--report-type-bg": category.soft } }
          >
            <span className="reports-all__badge-dot" />
            { typeName }
          </span>
        )
      }
    },
    {
      key: 'class_name',
      title: 'Class',
      render: (record) => {
        return record.class_name
          ? <span className="reports-all__class-chip">{ record.class_name }</span>
          : <span className="reports-all__empty-class">-</span>
      }
    },
    {
      key: trans(Commons.actions),
      title: trans(Commons.actions), fixed: 'right',

      render: (record) => {
        const actionItems = [
          {
            key: "generate",
            label: "Generate or Download",
            icon: <DownloadOutlined />,
            onClick: () => generateReport(record),
          },
          checkPermissions('reportsAll', 'update') ? {
            key: "edit",
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => onEdit(record),
          } : null,
          checkPermissions('reportsAll', 'update') ? {
            key: "schedule",
            label: "Schedule Report",
            icon: <CalendarOutlined />,
            onClick: () => scheduleReport(record),
          } : null,
          authUser.super_admin ? {
            key: "copy",
            label: "Copy Report to other clients",
            icon: <CopyOutlined />,
            onClick: () => onCopyReport(record),
          } : null,
        ].filter(Boolean);

        return (
          <Dropdown
            trigger={ ["click"] }
            placement="bottomRight"
            dropdownRender={ () => (
              <div className="reports-all__action-menu">
                { actionItems.map((item) => (
                  <button
                    key={ item.key }
                    type="button"
                    className="reports-all__action-item"
                    onClick={ item.onClick }
                  >
                    { item.icon }
                    <span>{ item.label }</span>
                  </button>
                )) }
                { !record.is_pre_defined && checkPermissions('reportsAll', 'delete') ? (
                  <Popconfirm title="Are you sure?" onConfirm={ () => onDelete(record) }>
                    <button type="button" className="reports-all__action-item reports-all__action-item--danger">
                      <DeleteOutlined />
                      <span>Delete</span>
                    </button>
                  </Popconfirm>
                ) : null }
              </div>
            ) }
          >
            <Button
              type="text"
              className="reports-all__kebab"
              aria-label={ `Open actions for ${record.title}` }
              icon={ <MoreOutlined /> }
            />
          </Dropdown>
        )
      },
    },
  ];

  // ==================================
  //   Table Data End
  // ==================================

  const buildFilters = (searchValue = searchTerm, reportTypeId = selectedReportType) => {
    const filters = [];
    const normalizedSearchValue = typeof searchValue === "string"
      ? searchValue
      : searchValue?.name || "";

    if (normalizedSearchValue.trim()) {
      filters.push(`title_|${normalizedSearchValue.trim()}`);
    }

    if (reportTypeId) {
      filters.push(`report_type_id_|${reportTypeId}`);
    }

    return filters.join(";");
  };

  useEffect(() => {
    getReportTypes();
    getDownloads();
    getScheduledReportsCount();
    // eslint-disable-next-line
  }, [props]);

  useEffect(() => {
    getAllData();
    // eslint-disable-next-line
  }, [props, currentPage, searchTerm, selectedReportType]);

  const getAllData = () => {
    setChildComponent(null)
    const payload = {
      page: currentPage,
      filters: buildFilters(),
      scope: props.scope,
    }
    makeRequest(setLoader, getAllDataReq, payload, onSuccess, onGetAllDataError);
  };
  const onSuccess = (response) => {
    setPagination(response.reports)
    setDataSource(response.reports.data);
  };
  const onGetAllDataError = (err, res) => {
    message.error(res.response.data.message);
  }
  const getReportTypes = () => {
    makeRequest(setLoader, getReportTypesAndTables, { scope: props.scope }, onGetReportTypesSuccess, onGetReportTypesError);
  };
  const onGetReportTypesSuccess = (response) => {
    setReportTypes(response.report_types || []);
  };
  const onGetReportTypesError = (err, res) => {
    message.error(res?.response?.data?.message || "Unable to load report types");
  };
  const getScheduledReportsCount = () => {
    makeRequestStateless(getReportSchedulesReq, { page: 1, scope: props.scope }, onScheduledReportsCountSuccess, () => {
      setScheduledReportsCount(0);
    });
  };
  const onScheduledReportsCountSuccess = (response) => {
    setScheduledReportsCount(response?.report_schedules?.total || 0);
  };
  //update raw report
  const onEdit = (record) => {
    if (record.is_raw_query && record.query) {
      setChildComponent(<UpdateRawReport onCompleted={ onCompleted } record={ record } disabled={ false } />);
    } else {
      setChildComponent(<UpdateReportName onCompleted={ onCompleted } record={ record } disabled={ false } />);
    }
  };


  const onCopyReport = (record) => {
    setChildComponent(<CopyReportToSaas onCompleted={ onCompleted } record={ record } disabled={ false } />);
  };
  const onCreatePredefinedReport = () => {
    setChildComponent(<CreatePredefinedReport onCompleted={ onCompleted } disabled={ false } />);
  };
  //view 
  // const onViewRawReport = (record) => {
  //   setChildComponent(<UpdateRawReport onCompleted={ onCompleted } record={ record } disabled={ true } />);
  // };

  // const onView = (record) => {
  //   // setChildComponent(<UpdateAttendanceLogs onCompleted={ onCompleted } record={ record } authUser={ authUser } allData={ allData } disabled={ true } />);
  // };

  const onDelete = (record) => {
    const payload = {
      filters: `employee_|${record.employment_id}`,
      id: record.id,
      scope: props.scope,
    }
    makeRequest(setLoader, deleteDataReq, payload, onDeleted, onError);
  };
  const onDeleted = (res) => {
    setDataSource(res.reports.data);
    getReportTypes();
    message.success("Record Deleted")
  };

  const onError = (err, res) => {
    message.error(res.response.data.message);
    //setErrors(errorList);
  }

  // Create component modal
  const onCreateCustomReport = () => {
    setChildComponent(<CreateCustomReport onCompleted={ onCompleted } />);
  };
  const onCreateRawReport = () => {
    setChildComponent(<CreateRawReport onCompleted={ onCompleted } />);
  };

  const onCompleted = (data) => {
    // if (data && data.length) {
    //   setDataSource(data);
    // }
    if (data) {
      getAllData();
      getReportTypes();
      getScheduledReportsCount();
    }

    setChildComponent(null);
  };
  //schedule report
  const scheduleReport = (record) => {
    setChildComponent(<ScheduleReport onCompleted={ onCompleted } record={ record } scope={ props.scope } disabled={ false } />);
  }
  const generateReport = (record) => {
    const selectedReport = {
      id: record.id,
      title: record.title,
      report_type_id: record.report_type_id || record.report_type?.id || null
    };

    setSelectedReportId(record.id);

    if (record.is_pre_defined && !record.query) {
      navigate(`generate-class/${record.id}`, {
        state: {
          report: record,
          previewMode: "class-based-download",
          selectedReport,
          scope: props.scope,
          returnTo: reportListPath,
        }
      });
      return;
    }

    navigate(`generate/${record.id}`, {
      state: {
        report: record,
        previewMode: 'preview-before-download',
        selectedReport,
        scope: props.scope,
        returnTo: reportListPath,
      }
    });
  }
  const getDownloads = () => {
    makeRequest(setLoader, getReportsFromJobsReq, { employment_id: authUser?.employment_id }, onSuccessReports, onErrorReports);
  }

  const onSuccessReports = (data, res) => {
  }
  const onErrorReports = (err) => {

  }
  // const viewDownloads=()=>{
  //   setDownloadsComponent(<ViewReports data={reportJobs} onCompleted={ onCompleted }/>)
  // }

  const onSearchReport = (value) => {
    setSearchInput(value);
    debouncedGetReports(value)
  }

  const onSearchSubmit = (values) => {
    const submittedValue = values?.name || "";
    debouncedGetReports.cancel();
    setSearchInput(submittedValue);
    setCurrentPage(1);
    setSearchTerm(submittedValue);
  };

  const debouncedGetReports = useMemo(
    () => _.debounce((value) => {
      setCurrentPage(1);
      setSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    return () => debouncedGetReports.cancel();
  }, [debouncedGetReports]);

  useEffect(() => {
    searchForm.setFieldsValue({ name: searchInput });
  }, [searchForm, searchInput]);

  useEffect(() => {
    const returnReport = location.state?.selectedReport;

    if (!returnReport) {
      return;
    }

    const reportTitle = returnReport.title || "";

    debouncedGetReports.cancel();
    setSelectedReportId(returnReport.id || null);
    setSearchInput(reportTitle);
    setSearchTerm(reportTitle);
    setCurrentPage(1);

    navigate(location.pathname, { replace: true, state: null });
  }, [debouncedGetReports, location.pathname, location.state, navigate]);

  const onReportTypeChange = (reportTypeId) => {
    debouncedGetReports.cancel();
    setSearchInput("");
    setSearchTerm("");
    searchForm.setFieldsValue({ name: "" });
    setCurrentPage(1);
    setSelectedReportType(reportTypeId);
  };

  const viewTabs = [
    { key: "reports", label: "Reports", count: pagination.total || 0 },
    { key: "scheduled", label: "Scheduled Reports", count: scheduledReportsCount },
  ];

  // Global reports can use the workspace strip for its Reports/Scheduled switch.
  // Scoped module reports keep the module toolbar visible and render this switch
  // inside the page header instead.
  const shouldUseWorkspaceViewTabs = !reportScope;
  const renderViewTabs = (variant) => viewTabs.map((tab) => {
    const isActive = activeTab === tab.key;
    const baseClass = variant === "workspace" ? "module-workspace__tab" : "reports-all__view-switch-item";

    return (
      <button
        key={ tab.key }
        type="button"
        role="tab"
        aria-selected={ isActive }
        className={ [baseClass, isActive ? `${baseClass}--active` : ""].filter(Boolean).join(" ") }
        onClick={ () => setActiveTab(tab.key) }
      >
        <span>{ tab.label }</span>
        <span className="reports-all__tab-count">{ tab.count }</span>
      </button>
    );
  });

  const tabsInWorkspaceStrip = useWorkspacePageTabs(
    shouldUseWorkspaceViewTabs ? <>{ renderViewTabs("workspace") }</> : null,
    [shouldUseWorkspaceViewTabs, activeTab, pagination.total, scheduledReportsCount]
  );

  const refreshReports = () => {
    getReportTypes();
    getAllData();
    getScheduledReportsCount();
  };

  return (
    <>

      { childComponent }
      <BodyComponent pagination={ pagination } setCurrentPage={ setCurrentPage } currentPage={ currentPage } filters="">
        <HeaderComponent headers={ pageHeaders } modkey={ props.modkey }>

          { shouldUseWorkspaceViewTabs && tabsInWorkspaceStrip ? null : (
            <div className="reports-all__view-switch" role="tablist" aria-label={ reportScope?.label || "All Reports" }>
              { renderViewTabs("switch") }
            </div>
          ) }

          { activeTab === "reports" && <>
            <RefreshButton onClick={ refreshReports } />
            <CreateButton className="reports-all__toolbar-secondary" onClick={ onCreateRawReport } text="Add Raw Report" />
            { authUser.super_admin || process.env.REACT_APP_ENV === "local" ? (
              <CreateButton className="reports-all__toolbar-secondary" onClick={ onCreatePredefinedReport } text="Add Predefined Report" />
            ) : '' }
            <CreateButton className="reports-all__toolbar-primary" onClick={ onCreateCustomReport } text="Add Custom Report" />
          </> }

          {/* <Popover content={ downloadsComponent } title="Downloads" trigger="click" placement="right" onClick={()=>viewDownloads()}>
          <ButtonComponent text={`Downloads(${reportJobs.length})`} />
        </Popover> */}
          {/* <FilterButton onClick={ onOpenFilter } /> */ }
        </HeaderComponent>
        <div className="reports-all__tabs">
          { activeTab === "reports" ? (
            <div className="reports-all">
              <aside className="reports-all__rail">
                <div className="reports-all__rail-title">{ reportScope?.label || "Report Type" }</div>
                <Input
                  className="reports-all__rail-search"
                  prefix={ <SearchOutlined /> }
                  placeholder="Filter types..."
                  allowClear
                  value={ reportTypeSearch }
                  onChange={ (e) => setReportTypeSearch(e.target.value) }
                />
                <div className="reports-all__type-list">
                  { reportTypeOptions.map((reportType) => {
                    const isActive = selectedReportType === reportType.id;
                    return (
                      <button
                        type="button"
                        key={ reportType.id || "all" }
                        className={ [
                          "reports-all__type-row",
                          isActive ? "reports-all__type-row--active" : "",
                        ].filter(Boolean).join(" ") }
                        onClick={ () => onReportTypeChange(reportType.id) }
                      >
                        <span
                          className="reports-all__type-dot"
                          style={ { backgroundColor: reportType.category.color } }
                        />
                        <span className="reports-all__type-name" title={ reportType.name }>{ reportType.name }</span>
                        <span className="reports-all__type-count">{ reportType.count }</span>
                      </button>
                    );
                  }) }
                </div>
              </aside>
              <section className="reports-all__panel">
                <Form form={ searchForm } { ...formLayout } onFinish={ onSearchSubmit } className="reports-all__search-form">
                  <Form.Item name="name" className="reports-all__search-item">
                    <Input
                      prefix={ <SearchOutlined /> }
                      placeholder="Search report by name"
                      value={ searchInput }
                      onChange={ (e) => onSearchReport(e.target.value) }
                    />
                  </Form.Item>
                  <div className="reports-all__active-filter">
                    <span className="reports-all__active-prefix">Showing</span>
                    <button
                      type="button"
                      className="reports-all__active-chip"
                      onClick={ () => onReportTypeChange(null) }
                      aria-label="Clear report type filter"
                    >
                      { activeFilterLabel } · { activeFilterCount }
                      <CloseOutlined />
                    </button>
                  </div>
                </Form>
                <TableComponent modkey={ props.modkey }
                  loader={ loader }
                  columns={ columns }
                  dataSource={ dataSource }
                  rowClassName={ (record) => record.id === selectedReportId ? "ant-table-row-selected" : "" }
                  onRow={ (record) => ({
                    style: {
                      cursor: "pointer",
                      background: record.id === selectedReportId ? "#edf5ff" : "transparent",
                    },
                  }) }
                />
              </section>
            </div>
          ) : (
            <ScheduledReportsManagement onCountChange={ setScheduledReportsCount } modkey={ props.modkey } scope={ props.scope } />
          ) }
        </div>

      </BodyComponent>
      { activeTab === "reports" && <PaginationComponent pagination={ pagination } modkey={ props.modkey } setCurrentPage={ setCurrentPage } currentPage={ currentPage } /> }
    </>
  );
};

export default IndexAllReports;


const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left"
}
